import { useMutation, useQuery } from '@tanstack/react-query'
import { useDebounced } from '@/hooks/use-debounced'
import { ApiError } from '@/services/api/client'
import {
  type ManualEntityRequest,
  type ManualGraphRefusal,
  type ManualRelationshipRequest,
  manualGraphApi,
} from '@/services/api/manual-graph'

/** The schema refusal inside a 422, or null for any other failure. */
export function refusalOf(error: unknown): ManualGraphRefusal | null {
  if (!(error instanceof ApiError) || error.status !== 422) return null
  const detail = (error.data as { detail?: unknown } | undefined)?.detail
  if (detail && typeof detail === 'object' && 'code' in detail) {
    const refusal = detail as Partial<ManualGraphRefusal>
    return {
      code: String(refusal.code),
      message: String(refusal.message ?? ''),
      fields: refusal.fields ?? {},
    }
  }
  return null
}

/** Create one entity of a schema type, in the signed-in user's name. */
export function useCreateManualEntity(caseId: string) {
  return useMutation({
    mutationFn: (body: ManualEntityRequest) => manualGraphApi.createEntity(caseId, body),
  })
}

/** Link two entities with a relation the schema allows, in the signed-in user's name. */
export function useCreateManualRelationship(caseId: string) {
  return useMutation({
    mutationFn: (body: ManualRelationshipRequest) =>
      manualGraphApi.createRelationship(caseId, body),
  })
}

/**
 * Entities that may already be the one being typed, looked up once typing
 * pauses. `request` is null while the form holds nothing to match on.
 *
 * The request is debounced as its JSON: the form builds a fresh object on every
 * render, and debouncing the object itself would restart the timer forever.
 */
export function useEntityDuplicates(caseId: string, request: ManualEntityRequest | null) {
  const debouncedKey = useDebounced(request ? JSON.stringify(request) : null, 450)
  return useQuery({
    queryKey: ['case', caseId, 'manual-graph', 'duplicates', debouncedKey],
    queryFn: () =>
      manualGraphApi.findDuplicates(
        caseId,
        JSON.parse(debouncedKey as string) as ManualEntityRequest
      ),
    enabled: debouncedKey !== null,
    staleTime: 30_000,
    select: data => data.candidates,
  })
}
